<template>
    <div class="flex min-h-screen w-full items-center justify-center text-center font-sans">
        <div v-if="loading">
            <div class="flex w-full flex-col items-center justify-center">
                <h1 class="mb-4 text-2xl font-bold">Please wait...</h1>
                <LoadingSpinner
                    class="fa-3x mb-4"
                    size-classes="w-12 h-12"
                />
                <h2 class="text-lg">Please do not close this window.</h2>
            </div>
        </div>
        <div
            v-else-if="message"
            class="w-full"
        >
            <h2 class="mb-4 text-xl">{{ message }}</h2>
            <a
                class="btn btn-brand btn-circle w-100 text-base"
                :href="props.redirectUrl"
            >
                Return to the portal
            </a>
        </div>
    </div>
</template>
<script setup lang="ts">
import { isMobile } from '@/components/Composables/MobileScreenDetection';
import LoadingSpinner from '@/components/Icons/LoadingSpinner.vue';
import { useFetch } from '@vueuse/core';
import { onMounted, ref } from 'vue';

const VALID_USER_EVENT = 'signed'; // this is the only user event we expect to see in a successful response

enum ErrorMessages {
    INVALID_RESPONSE = 'Invalid response from e-signature callback. Missing document data.',
    GENERIC_ERROR = 'An error occurred while processing the e-signature callback.',
}

export interface ESignDocumentMessageResponse {
    document_id?: number;
    signed_at: string;
    error?: string;
}

export interface ESignDocumentAPIResponse extends ESignDocumentMessageResponse {
    event?: string;
    user_event?: string; // 'signed' - will only ever show up in a successful response. found in app/Http/Controllers/ClientController.php
    message?: string;
}

interface ESignDocumentMessage {
    document: ESignDocumentAPIResponse;
}

const props = defineProps<{
    processESignCallbackUrl: string;
    redirectUrl: string;
}>();
const message = ref<string>();
const loading = ref<boolean>(true);
const esignChannel = ref<BroadcastChannel>();

const handleMessageCallback = (data: { document: ESignDocumentMessageResponse }, redirectURL: string): void => {
    if (esignChannel.value) {
        esignChannel.value.postMessage(data.document);
        // only close the window if it's not a mobile flow, because mobile opens in a popup iframe.
        if (!isMobile.value) {
            return window.close();
        }
        window.location.href = redirectURL;
        return;
    }
    if (data.document.signed_at && data.document.document_id) {
        // only set signed to true if the document was signed. The success flow could also be triggered by a "Save For Later" action in docusign.
        // this will trigger a reload of the ViewFilesPage.vue component in the client portal.
        localStorage.setItem('signed', 'true');
    }
    message.value = data.document.error || 'Saved, you may safely close this page.';
};

onMounted(async () => {
    if ('BroadcastChannel' in window) {
        esignChannel.value = new BroadcastChannel('esign_status');
    }
    try {
        const { data, error } = await useFetch(props.processESignCallbackUrl).json<ESignDocumentMessage>();
        // this is the successful sign response
        if (data.value?.document?.user_event === VALID_USER_EVENT) {
            handleMessageCallback(data?.value, props.redirectUrl);
        } else if (data.value) {
            // if we don't have a user_event, we assume it's an error or a different type of response
            const errorMessage = data.value?.document?.message || ErrorMessages.GENERIC_ERROR;
            throw new Error(errorMessage);
        } else {
            const errorMessage = error.value?.message || ErrorMessages.GENERIC_ERROR;
            throw new Error(errorMessage);
        }
    } catch (e) {
        const errorMessage = e instanceof Error ? e.message : ErrorMessages.GENERIC_ERROR;
        message.value = errorMessage;
        handleMessageCallback(
            {
                document: {
                    document_id: undefined,
                    signed_at: '',
                    error: errorMessage,
                },
            },
            props.redirectUrl,
        );
    }
    loading.value = false;
});
</script>
